import { FunctionComponent, useMemo, useRef } from 'react';
import { clsx } from 'clsx';
import { WizardStepTree, WizardStepTreeSnapshot } from '../interfaces/wizard-step';
import { wizardHeaderDataIds } from './data-ids';
import { getWizardCurrentStepInfo } from '../utils/wizardStepUtil';
import { WizardHeaderStep } from './WizardHeaderStep/WizardHeaderStep';
import styles from './WizardHeader.module.scss';
import { WizardHeaderStepConnector } from './WizardHeaderStepConnector/WizardHeaderStepConnector';

export interface WizardHeaderProps {
  steps: WizardStepTree;
  stepTreeSnapshot?: WizardStepTreeSnapshot;
  Left?: FunctionComponent<object>;
  leftProps?: object;
  selectStepGroup?: (groupIndex: number) => void;
}

export function WizardHeader({
  steps,
  stepTreeSnapshot,
  selectStepGroup,
  Left,
  leftProps = {}
}: WizardHeaderProps) {
  const wizardHeaderStepRef = useRef<HTMLDivElement>(null);

  const { currentStepIndex, currentStepGroup, currentStepGroupIndex } = useMemo(() => {
    return getWizardCurrentStepInfo(stepTreeSnapshot);
  }, [stepTreeSnapshot]);

  const totalStepsInCurrentGroup = currentStepGroup?.children.length || 1;

  const stepsProgress = useMemo(() => {
    return steps?.children.map((_, index) => {
      if (!stepTreeSnapshot) {
        return -1;
      }

      if (index < currentStepGroupIndex) {
        return 1;
      }

      if (index > currentStepGroupIndex) {
        return -1;
      }

      return currentStepIndex / totalStepsInCurrentGroup;
    });
  }, [steps, stepTreeSnapshot, currentStepIndex, currentStepGroupIndex, totalStepsInCurrentGroup]);

  return (
    <div className={styles.wizardHeader} data-id={wizardHeaderDataIds.container}>
      {Left ? (
        <div className={styles.leftComponent}>
          <Left {...leftProps} />
        </div>
      ) : null}

      <div
        className={clsx('center-flex-row position-relative', styles.wizardHeaderSteps)}
        ref={wizardHeaderStepRef}
      >
        {steps.children.map((step, index) => (
          <WizardHeaderStep
            key={step.id}
            step={step}
            index={index}
            progress={stepsProgress[index]}
            onSelect={() => {
              if (selectStepGroup) {
                selectStepGroup(index);
              }
            }}
            Extra={step.Extra}
            extraProps={step.extraProps}
            disabled={index > currentStepGroupIndex}
          />
        ))}
      </div>

      <WizardHeaderStepConnector wizardHeaderStepRef={wizardHeaderStepRef} />
    </div>
  );
}
